import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { Button } from "@/components/kit";

const fmtDue = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null);

// Log garden hours straight from the snapshot card. Uses the plots the gardener manages.
function LogHoursForm({ memberId, plots, onLogged }) {
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState("");
  const [note, setNote] = useState("");
  const [plotId, setPlotId] = useState((plots[0] || {}).plot_id || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const h = parseFloat(hours);
    if (!h || h <= 0) return toast.error("Enter hours greater than 0");
    setBusy(true);
    try {
      await api.post("/garden-hours", { member_id: memberId, plot_id: plotId || null, date: new Date().toISOString().slice(0, 10), hours: h, note });
      toast.success(`Logged ${h} hour${h === 1 ? "" : "s"}`); setHours(""); setNote(""); setOpen(false); onLogged && onLogged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not log hours"); }
    finally { setBusy(false); }
  };
  if (!open) return <Button variant="secondary" data-testid="log-hours-btn" onClick={() => setOpen(true)} className="mt-4 px-3 py-2 min-h-[38px] text-[13px]">Log garden hours</Button>;
  return (
    <div className="mt-4 border-t border-ink/10 pt-3 space-y-2" data-testid="log-hours-form">
      <div className="flex gap-2">
        <input type="number" min="0" step="0.5" data-testid="log-hours-input" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="Hours" className="w-24 bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 text-[14px]" />
        {plots.length > 1 && <select data-testid="log-hours-plot" value={plotId} onChange={(e) => setPlotId(e.target.value)} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 text-[14px]">{plots.map((p) => <option key={p.plot_id} value={p.plot_id}>{p.location}</option>)}</select>}
      </div>
      <input data-testid="log-hours-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 text-[14px]" />
      <div className="flex gap-2"><Button data-testid="log-hours-save" onClick={save} disabled={busy} className="px-3 py-2 min-h-[38px] text-[13px]">{busy ? "Saving…" : "Save hours"}</Button><Button variant="secondary" onClick={() => setOpen(false)} className="px-3 py-2 min-h-[38px] text-[13px]">Cancel</Button></div>
    </div>
  );
}

// Garden snapshot for any gardener who manages one or more plots. Shows each plot's
// photo + theme, the viewer's own open tasks, and unfulfilled needs — all completable
// right here — and (on the member dashboard) a Log garden hours action. Self-hides
// when the signed-in person manages no plots.
export default function GardenSnapshot({ onNavigate, memberId }) {
  const [plots, setPlots] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get("/my/garden-snapshot"); setPlots(r.data.plots || []); }
    catch (e) { setPlots([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!plots || plots.length === 0) return null;

  const completeTask = async (pid, tid) => {
    try { await api.post(`/my/garden-snapshot/task/${pid}/${tid}/complete`); toast.success("Task completed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not complete task"); }
  };
  const completeNeed = async (pid, nid) => {
    try { await api.post(`/my/garden-snapshot/need/${pid}/${nid}/complete`); toast.success("Marked fulfilled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update need"); }
  };

  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="garden-snapshot">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Icon name="Sprout" className="w-5 h-5 text-dark-olive" />
          <h2 className="font-zilla text-xl font-semibold">My garden snapshot</h2>
        </div>
        <button data-testid="see-all-gardens" onClick={() => onNavigate && onNavigate("garden_plots")}
          className="shrink-0 text-[13px] font-semibold text-dark-olive hover:text-ink inline-flex items-center gap-1">
          See all gardens <Icon name="ChevronRight" className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-4">
        {plots.map((p) => (
          <div key={p.plot_id} data-testid={`snapshot-plot-${p.plot_id}`} className="bg-warm-cream border border-ink/10 rounded-xl overflow-hidden">
            <div className="flex gap-3 p-3">
              {p.photo_url
                ? <img src={p.photo_url} alt="" className="w-20 h-20 rounded-lg object-cover bg-olive/10 shrink-0" />
                : <div className="w-20 h-20 rounded-lg bg-olive/15 flex items-center justify-center shrink-0"><Icon name="Sprout" className="w-7 h-7 text-olive" /></div>}
              <div className="min-w-0">
                <div className="text-[16px] font-semibold truncate">{p.location}</div>
                <div className="text-[13px] text-ink/60 truncate">{p.theme || "No theme set"}</div>
              </div>
            </div>

            {p.tasks.length > 0 && (
              <div className="px-3 pb-2" data-testid={`snapshot-tasks-${p.plot_id}`}>
                <div className="text-[12px] uppercase tracking-wide text-ink/45 font-bold mb-1">My open tasks</div>
                <div className="space-y-1.5">
                  {p.tasks.map((t) => (
                    <div key={t.id} data-testid={`snapshot-task-${t.id}`} className="flex items-center justify-between gap-2 bg-cream border border-ink/10 rounded-lg px-3 py-2">
                      <div className="min-w-0">
                        <div className="text-[14px] truncate">{t.description}</div>
                        {t.due_date && <div className="text-[12px] text-ink/50">Due {fmtDue(t.due_date)}</div>}
                      </div>
                      <button data-testid={`snapshot-task-complete-${t.id}`} onClick={() => completeTask(p.plot_id, t.id)}
                        className="shrink-0 border border-dark-olive/40 text-dark-olive rounded-lg px-2.5 py-1.5 text-[12px] font-semibold">Complete</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {p.needs.length > 0 && (
              <div className="px-3 pb-3" data-testid={`snapshot-needs-${p.plot_id}`}>
                <div className="text-[12px] uppercase tracking-wide text-ink/45 font-bold mb-1">Outstanding needs</div>
                <div className="space-y-1.5">
                  {p.needs.map((n) => (
                    <div key={n.id} data-testid={`snapshot-need-${n.id}`} className="flex items-center justify-between gap-2 bg-cream border border-ink/10 rounded-lg px-3 py-2">
                      <div className="min-w-0"><span className="text-[12px] uppercase text-ink/45 font-bold mr-1">{n.category}</span><span className="text-[14px]">{n.text}</span></div>
                      <button data-testid={`snapshot-need-complete-${n.id}`} onClick={() => completeNeed(p.plot_id, n.id)}
                        className="shrink-0 border border-gold/50 text-ink rounded-lg px-2.5 py-1.5 text-[12px] font-semibold">Fulfilled</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {p.tasks.length === 0 && p.needs.length === 0 && (
              <div className="px-3 pb-3 text-[13px] text-dark-olive flex items-center gap-1.5" data-testid={`snapshot-clear-${p.plot_id}`}>
                <Icon name="Check" className="w-4 h-4" /> Nothing needs you on this plot.
              </div>
            )}
          </div>
        ))}
      </div>

      {memberId && <LogHoursForm memberId={memberId} plots={plots} onLogged={load} />}
    </div>
  );
}
